import React, { useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormGetValues,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  FormSelect,
  VirtualizedFormSelect,
  Alert,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
  ChevronDown,
} from 'lucide-react';
import type {
  KinnserClinicDataResponse,
  KinnserCreatePatientResponse,
} from '../../services/api/types.gen';
import { DateField } from '../../components/FormFields';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import { IntakeValidationErrorList } from '../../components/intake/IntakeValidationErrorList';
import { KinnserAdmissionSourceField } from '../../components/intake/KinnserAdmissionSourceField';
import { useAdmissionSourceRowHintState } from '../../components/intake/kinnserAdmissionSource';
import { KinnserReferralSourceFields } from '../../components/intake/KinnserReferralSourceFields';
import { formErrorsToValidationItems } from '../../components/intake/intakeValidationErrorItems';

/** Intake shape needed for Kinnser auto-add APIs. */
interface SelectedIntakeForAutoAdd {
  id?: string;
  kinnser_matched_patient_key?: string | null;
  kinnser_clinic_id?: string | null;
  agency_clinic?: { clinic_key?: string } | null;
  patient_notes?: string | null;
  kinnser_soc_date?: string | null;
  kinnser_episode_start_date?: string | null;
  /** The UB-04 code intake captured; read by the Admission Source notice. */
  discharge_facility_type?: string | null;
  /** The saved override, which is what the push actually resolves to. */
  kinnser_admission_source?: string | null;
  fields?: Record<string, unknown> | null;
}

interface KinnserSchedulingStepFormContentProps {
  // Form controls
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  getValues: UseFormGetValues<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Save status
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;

  // Clinics
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;

  // Kinnser creation
  createResult: KinnserCreatePatientResponse | null;

  // Episode creation flag for conditional validation
  kinnserCreateEpisode?: boolean;

  // Whether this intake has been confirmed as an existing patient
  isExistingPatient?: boolean;

  // For existing patient: call auto-add episode / admission+episode APIs
  selectedIntake?: SelectedIntakeForAutoAdd | null;
  kinnserCreationType?:
    'new_admission' | 'new_episode' | 'current_episode' | null;
  /** Min date for episode start (e.g. day after prior episode start); YYYY-MM-DD. */
  minEpisodeStartDate?: string | null;
  /** Min date for SOC (e.g. day after prior discharge/start of care); YYYY-MM-DD. */
  minSocDate?: string | null;
  /** When true (new episode + matched episode not discharged), SOC date is locked to matched episode's start date. */
  socDateLockedToMatchedEpisode?: boolean;
  /** When socDateLockedToMatchedEpisode is true, this is the date to use and display (YYYY-MM-DD). */
  lockedSocDateStr?: string | null;
  /** When true (e.g. new episode with matched end date), episode start is locked to day after matched episode end. */
  episodeStartLockedToMatchedEnd?: boolean;
  /** When episodeStartLockedToMatchedEnd is true, this is the episode start date to use (YYYY-MM-DD). */
  lockedEpisodeStartDateStr?: string | null;
  /** When true (SOC + new admission + existing patient), episode start is locked to the Start of Care date. */
  episodeStartLockedToSoc?: boolean;
  /** When true (WRM-enabled clinic), hide the SOC date field. */
  hideSocDate?: boolean;

  /** Scroll to a field when the user clicks a validation link. */
  onScrollToField?: (fieldName: string) => void;
}

export const KinnserSchedulingStepFormContent: React.FC<
  KinnserSchedulingStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  getValues: _getValues,
  handleFieldBlur,
  saveToServer,
  clinicData,
  loadingClinicData,
  createResult,
  isExistingPatient = false,
  selectedIntake,
  kinnserCreationType,
  minEpisodeStartDate,
  minSocDate,
  socDateLockedToMatchedEpisode = false,
  lockedSocDateStr = null,
  episodeStartLockedToMatchedEnd = false,
  lockedEpisodeStartDateStr = null,
  episodeStartLockedToSoc = false,
  hideSocDate = false,
  onScrollToField,
}) => {
  // WRM hides the SOC date field; episode start is then the editable source and
  // useSchedulingStepForm keeps kinnser_soc_date in sync. Do not disable episode
  // start "to match SOC" when the user has no SOC control.
  const episodeStartLockedToSocForUi = episodeStartLockedToSoc && !hideSocDate;

  // Hooks cannot be gated, so this runs even on the paths that do not render
  // the row; it is two subscriptions and a string compare.
  const admissionSourceHint = useAdmissionSourceRowHintState(
    control,
    selectedIntake
  );

  const episodeStartManuallySetRef = useRef(false);
  const saveToServerRef = useRef(saveToServer);
  saveToServerRef.current = saveToServer;

  useEffect(() => {
    episodeStartManuallySetRef.current = false;
  }, [selectedIntake?.id]);

  const episodeStartMinDate =
    minEpisodeStartDate != null && minEpisodeStartDate !== ''
      ? (() => {
          const [y, m, d] = minEpisodeStartDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : undefined;

  const socMinDate =
    minSocDate != null && minSocDate !== ''
      ? (() => {
          const [y, m, d] = minSocDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : undefined;
  const watchedSocDate = useWatch({
    control,
    name: 'kinnser_soc_date',
  });

  // Normalize date to YYYY-MM-DD for comparison (avoid redundant saves when intake already has value).
  const intakeSocDateOnly =
    selectedIntake?.kinnser_soc_date != null
      ? String(selectedIntake.kinnser_soc_date).split('T')[0]
      : null;
  const intakeEpisodeStartOnly =
    selectedIntake?.kinnser_episode_start_date != null
      ? String(selectedIntake.kinnser_episode_start_date).split('T')[0]
      : null;

  // When SOC is locked to matched episode (new episode + not discharged), keep form and intake in sync.
  // Only persist when locked and intake doesn't already have this value to avoid redundant requests.
  useEffect(() => {
    if (!socDateLockedToMatchedEpisode || !lockedSocDateStr) return;
    setValue('kinnser_soc_date', lockedSocDateStr);
    if (intakeSocDateOnly !== lockedSocDateStr) {
      void saveToServerRef.current({ kinnser_soc_date: lockedSocDateStr });
    }
  }, [
    socDateLockedToMatchedEpisode,
    lockedSocDateStr,
    setValue,
    intakeSocDateOnly,
  ]);

  // When episode start is locked to day after matched end, keep form and intake in sync.
  // Only persist when intake doesn't already have this value to avoid redundant requests.
  useEffect(() => {
    if (!episodeStartLockedToMatchedEnd || !lockedEpisodeStartDateStr) return;
    setValue('kinnser_episode_start_date', lockedEpisodeStartDateStr);
    if (intakeEpisodeStartOnly !== lockedEpisodeStartDateStr) {
      void saveToServerRef.current({
        kinnser_episode_start_date: lockedEpisodeStartDateStr,
      });
    }
  }, [
    episodeStartLockedToMatchedEnd,
    lockedEpisodeStartDateStr,
    setValue,
    intakeEpisodeStartOnly,
  ]);

  // SOC + new admission (existing patient): episode start must match Start of Care.
  // Skipped when hideSocDate (WRM): SOC is synced from episode start elsewhere.
  useEffect(() => {
    if (!episodeStartLockedToSocForUi) return;
    const socNorm =
      watchedSocDate && typeof watchedSocDate === 'string'
        ? watchedSocDate.split('T')[0]
        : null;
    if (!socNorm) return;
    setValue('kinnser_episode_start_date', socNorm);
    if (intakeEpisodeStartOnly !== socNorm) {
      void saveToServerRef.current({
        kinnser_episode_start_date: socNorm,
      });
    }
  }, [
    episodeStartLockedToSocForUi,
    watchedSocDate,
    setValue,
    intakeEpisodeStartOnly,
  ]);

  return (
    <div className='flex flex-col'>
      {/* Validation errors */}
      {!isExistingPatient && Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1 text-red-800'>
              <IntakeValidationErrorList
                title='Missing fields required to schedule:'
                ariaLabel='Validation errors'
                items={formErrorsToValidationItems(errors, onScrollToField)}
              />
            </div>
          </div>
        </Alert>
      )}
      {/* Validation errors or success message */}
      {createResult && (
        <div className='mb-3'>
          {createResult.success ? (
            <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
              <div className='flex items-center gap-2 text-green-800'>
                <CheckCircle className='h-4 w-4' />
                <span className='font-medium'>
                  Patient created successfully in Kinnser
                </span>
              </div>
              {createResult.kinnser_patient_id && (
                <div className='text-sm text-green-700 mt-1'>
                  Kinnser Patient ID: {createResult.kinnser_patient_id}
                </div>
              )}
            </div>
          ) : (
            <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
              <div className='flex items-center gap-2 text-red-800 mb-2'>
                <XCircle className='h-4 w-4' />
                <span className='font-medium'>Validation Errors</span>
              </div>
              <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                {createResult.errors?.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        {/* Schedule Start of Care */}
        <div className='grid grid-cols-4 gap-3'>
          {/* Row 1 & 2: Assignment fields (hidden for existing patients) */}
          {!isExistingPatient && (
            <>
              <VirtualizedFormSelect
                label='Clinician'
                placeholder='Select clinician...'
                options={clinicData?.clinicians || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_clinician_id?.message}
                {...register('kinnser_clinician_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='Clinical Care Manager'
                placeholder='Select clinical care manager...'
                options={clinicData?.clinical_care_managers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_clinical_care_manager_id?.message}
                {...register('kinnser_clinical_care_manager_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='Case Manager'
                placeholder='Select case manager...'
                options={clinicData?.case_managers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_case_manager_id?.message}
                {...register('kinnser_case_manager_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='OASIS Approver'
                placeholder='Select OASIS approver...'
                options={clinicData?.oasis_approvers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_oasis_approver_id?.message}
                {...register('kinnser_oasis_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              {/* Row 2: PT, OT, ST Approvers */}
              <VirtualizedFormSelect
                label='PT Approver'
                placeholder='Select PT approver...'
                options={clinicData?.pt_approvers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_pt_approver_id?.message}
                {...register('kinnser_pt_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='OT Approver'
                placeholder='Select OT approver...'
                options={clinicData?.ot_approvers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_ot_approver_id?.message}
                {...register('kinnser_ot_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='ST Approver'
                placeholder='Select ST approver...'
                options={clinicData?.st_approvers || []}
                sortOptions
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_st_approver_id?.message}
                {...register('kinnser_st_approver_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              {clinicData?.primary_schedulers &&
                clinicData.primary_schedulers.length > 0 && (
                  <VirtualizedFormSelect
                    label='Primary Scheduler'
                    placeholder='Select primary scheduler...'
                    options={clinicData.primary_schedulers}
                    sortOptions
                    required={true}
                    disabled={loadingClinicData}
                    error={errors.kinnser_primary_scheduler_id?.message}
                    {...register('kinnser_primary_scheduler_id', {
                      onBlur: handleFieldBlur,
                    })}
                  />
                )}

              {clinicData?.secondary_schedulers &&
                clinicData.secondary_schedulers.length > 0 && (
                  <VirtualizedFormSelect
                    label='Secondary Scheduler'
                    placeholder='Select secondary scheduler...'
                    options={clinicData.secondary_schedulers}
                    sortOptions
                    required={true}
                    disabled={loadingClinicData}
                    error={errors.kinnser_secondary_scheduler_id?.message}
                    {...register('kinnser_secondary_scheduler_id', {
                      onBlur: handleFieldBlur,
                    })}
                  />
                )}
            </>
          )}

          {!hideSocDate && (
            <>
              {/* Row 3: Type of SOC — editable for new patients; read-only for existing (Kinnser assigns in EMR) */}
              {isExistingPatient ? (
                <div className='flex flex-col gap-1'>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-xs font-medium text-gray-700'>
                      Type of SOC
                    </span>
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type='button'
                            className='inline-flex text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400 rounded'
                            aria-label='Type of SOC is assigned in Kinnser'
                          >
                            <Info className='h-3.5 w-3.5' />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side='top' className='max-w-xs text-xs'>
                          Automatically assigned in Kinnser
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {/* Hidden field keeps RHF/intake in sync; visible shell omits SN/PT so we do not
                      imply a value that may differ from what Kinnser assigns. */}
                  <input
                    type='hidden'
                    {...register('kinnser_type_of_soc', {
                      onBlur: handleFieldBlur,
                    })}
                  />
                  <div
                    className='flex h-8 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-muted-foreground opacity-60 cursor-not-allowed'
                    aria-label='Type of SOC is automatically assigned in Kinnser'
                  >
                    <span className='min-w-0 flex-1' aria-hidden />
                    <ChevronDown
                      className='h-4 w-4 shrink-0 opacity-50'
                      aria-hidden
                    />
                  </div>
                  {errors.kinnser_type_of_soc?.message ? (
                    <p className='text-xs text-red-600'>
                      {errors.kinnser_type_of_soc.message}
                    </p>
                  ) : null}
                </div>
              ) : (
                <FormSelect
                  label='Type of SOC'
                  placeholder='Select SOC type...'
                  options={[
                    { value: 'SN', label: 'Nursing' },
                    { value: 'PT', label: 'Therapy' },
                  ]}
                  required={true}
                  disabled={loadingClinicData}
                  error={errors.kinnser_type_of_soc?.message}
                  {...register('kinnser_type_of_soc', {
                    onBlur: handleFieldBlur,
                  })}
                />
              )}
            </>
          )}

          {isExistingPatient && (
            <VirtualizedFormSelect
              label='Clinician'
              placeholder='Select clinician...'
              options={clinicData?.clinicians || []}
              sortOptions
              required={true}
              disabled={loadingClinicData}
              error={errors.kinnser_clinician_id?.message}
              {...register('kinnser_clinician_id', {
                onBlur: handleFieldBlur,
              })}
            />
          )}

          {!hideSocDate && (
            <div className='flex flex-col gap-0.5'>
              <Controller
                name='kinnser_soc_date'
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <DateField
                    fieldAnchor='kinnser_soc_date'
                    label='Start of Care Date'
                    value={
                      socDateLockedToMatchedEpisode
                        ? (lockedSocDateStr ?? value)
                        : value
                    }
                    htmlFor='socDate'
                    required={true}
                    disabled={socDateLockedToMatchedEpisode}
                    error={errors.kinnser_soc_date?.message}
                    onChange={val => {
                      if (socDateLockedToMatchedEpisode) return;
                      onChange(val);
                      const toSave: Partial<SchedulingStepFormData> = {
                        kinnser_soc_date: val,
                      };
                      // Copy episode start when it must follow SOC, or whenever the user
                      // hasn’t overridden episode start (init often pre-fills episode start).
                      const norm =
                        val && typeof val === 'string'
                          ? val.split('T')[0]
                          : null;
                      if (
                        norm &&
                        !episodeStartLockedToMatchedEnd &&
                        (episodeStartLockedToSoc ||
                          !episodeStartManuallySetRef.current)
                      ) {
                        setValue('kinnser_episode_start_date', val, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        toSave.kinnser_episode_start_date = val;
                      }
                      saveToServer(toSave);
                    }}
                    onBlur={onBlur}
                    minDate={socMinDate}
                    yearRange={{
                      fromYear: new Date().getFullYear() - 1,
                      toYear: new Date().getFullYear() + 1,
                    }}
                  />
                )}
              />
              {socDateLockedToMatchedEpisode && (
                <p className='text-xs text-gray-700'>
                  Same as matched episode&apos;s start of care (episode not
                  discharged).
                </p>
              )}
            </div>
          )}

          <div className='flex flex-col gap-0.5'>
            <Controller
              name='kinnser_episode_start_date'
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <DateField
                  fieldAnchor='kinnser_episode_start_date'
                  label='Episode Start Date'
                  value={
                    episodeStartLockedToMatchedEnd
                      ? (lockedEpisodeStartDateStr ?? value)
                      : episodeStartLockedToSocForUi
                        ? (() => {
                            const s =
                              watchedSocDate &&
                              typeof watchedSocDate === 'string'
                                ? watchedSocDate.split('T')[0]
                                : null;
                            return s ?? value;
                          })()
                        : value
                  }
                  htmlFor='episodeStartDate'
                  required={true}
                  disabled={
                    episodeStartLockedToMatchedEnd ||
                    episodeStartLockedToSocForUi
                  }
                  error={errors.kinnser_episode_start_date?.message}
                  onChange={val => {
                    if (
                      episodeStartLockedToMatchedEnd ||
                      episodeStartLockedToSocForUi
                    )
                      return;
                    episodeStartManuallySetRef.current = Boolean(val);
                    onChange(val);
                    saveToServer({
                      kinnser_episode_start_date: val,
                    });
                  }}
                  onBlur={onBlur}
                  minDate={episodeStartMinDate}
                  yearRange={{
                    fromYear: new Date().getFullYear() - 1,
                    toYear: new Date().getFullYear() + 1,
                  }}
                />
              )}
            />
            {episodeStartLockedToMatchedEnd && (
              <p className='text-xs text-gray-700'>
                One day after matched episode&apos;s end date.
              </p>
            )}
            {episodeStartLockedToSocForUi && (
              <p className='text-xs text-gray-700'>
                Same as Start of Care date.
              </p>
            )}
          </div>

          {/* Referrals belong to an admission, so they only apply where this
              push creates one: an existing WellSky patient getting a new
              admission. The episode-only path has nowhere to put them, and
              Roger Doc's episode API takes no referral fields. Left unset, the
              admission keeps the referral WellSky carried forward from the
              prior one. Admission Source belongs to the same admission and
              rides in as the row's leading field, so the three sit on one line
              in the same order as the create-episode form. Full-width so the
              row divides the card rather than a quarter of it. */}
          {isExistingPatient && kinnserCreationType === 'new_admission' && (
            <div className='col-span-4'>
              <KinnserReferralSourceFields<SchedulingStepFormData>
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                clinicData={clinicData}
                loadingClinicData={loadingClinicData}
                // Roger Doc has no clear semantic on this path: an unset
                // referral means WellSky keeps the one carried forward from
                // the prior admission. Offering "clear" would read as removal.
                allowClear={false}
                // Admission source obeys the same carry-forward rule, so the
                // row's single "left unset" line has to account for it -- and
                // stand down when its own notice already says it.
                leadingFieldUnset={admissionSourceHint.isUnset}
                hideUnsetHint={admissionSourceHint.explainedByNotice}
                leadingField={
                  <KinnserAdmissionSourceField<SchedulingStepFormData>
                    control={control}
                    register={register}
                    errors={errors}
                    handleFieldBlur={handleFieldBlur}
                    saveToServer={saveToServer}
                    disabled={loadingClinicData}
                    selectedIntake={selectedIntake}
                    // Existing patient: the backend omits the key when this is
                    // unset, so the new admission inherits the prior
                    // admission's source rather than a WellSky default.
                    unsetOutcome='carried_forward'
                  />
                }
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
